import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ClipboardCheck, ChevronLeft, ChevronRight, Users, IndianRupee, Download, Sun, Coffee, PlaneTakeoff, Mail, GraduationCap, ShieldCheck, Clock } from "lucide-react";

const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const STATUS_META = {
  present:  { label: "Present",  color: "bg-emerald-500 text-white", icon: Sun,          short: "P" },
  leave:    { label: "Leave",    color: "bg-red-500 text-white",     icon: PlaneTakeoff, short: "L" },
  holiday:  { label: "Holiday",  color: "bg-amber-500 text-white",   icon: Coffee,       short: "H" },
};

function monthGrid(y, m) {
  const first = new Date(y, m, 1);
  const dayOfWeek = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - dayOfWeek);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user.real_role === "FOUNDER" || user.real_role === "BDOM";
  const canSeeParticipants = ["FOUNDER", "BDOM", "PCWM"].includes(user.real_role);
  const canSeeOthers = user.real_role === "FOUNDER" || user.real_role === "BDOM";   // Founder + BDOM browse everyone's attendance
  const [scope, setScope] = useState("team");   // 'team' | 'participants'

  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedUser, setSelectedUser] = useState(user.id);
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState([]);

  const ym = `${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,"0")}`;

  const staffOptions = useMemo(
    () => users.filter(u => !["PARTICIPANT","PARTNER"].includes(u.role)),
    [users]
  );

  const load = async () => {
    const [uRes, aRes, sRes] = await Promise.all([
      api.get("/users").catch(()=>({ data: [] })),
      api.get(`/attendance?ym=${ym}&user_id=${selectedUser}`),
      isAdmin ? api.get(`/attendance/summary?ym=${ym}`) : api.get(`/attendance/summary?ym=${ym}`),
    ]);
    setUsers(uRes.data);
    setAttendance(aRes.data);
    setSummary(sRes.data);
  };
  useEffect(() => { load(); }, [ym, selectedUser, isAdmin]);

  const byDate = useMemo(() => {
    const m = {};
    for (const a of attendance) m[a.date] = a;
    return m;
  }, [attendance]);

  const jump = (delta) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  const mark = async (dateISO, status) => {
    // For Leave, prompt for a reason so the founder can review context on
    // approval.  For Present, auto-attach a standard 9:30→18:30 shift so the
    // calendar shows "9h" without extra clicks.  Users can override the
    // times later by clicking the cell (a lightweight edit modal ships next).
    const body = { user_id: selectedUser, date: dateISO, status };
    if (status === "leave") {
      const reason = window.prompt("Reason for leave (visible to Founder / BDOM):", byDate[dateISO]?.leave_reason || "");
      if (reason === null) return;
      body.leave_reason = (reason || "").trim();
    }
    if (status === "present") {
      const existing = byDate[dateISO];
      body.check_in = existing?.check_in || "09:30";
      body.check_out = existing?.check_out || "18:30";
    }
    await api.post("/attendance", body);
    await load();
    toast.success(`${STATUS_META[status].label} — ${dateISO}${status === "present" ? " · 9.0h" : ""}`);
  };

  // Inline hours editor — opens a mini prompt so staff can log an actual
  // shift (e.g. 10:15→18:00 = 7.75h) instead of the default 9:30→18:30.
  const editHours = async (dateISO) => {
    const a = byDate[dateISO];
    if (!a || a.status !== "present") { toast.error("Mark this day Present first"); return; }
    const ci = window.prompt("Check-in time (HH:MM):", a.check_in || "09:30");
    if (ci === null) return;
    const co = window.prompt("Check-out time (HH:MM):", a.check_out || "18:30");
    if (co === null) return;
    await api.post("/attendance", { user_id: selectedUser, date: dateISO, status: "present", check_in: ci, check_out: co });
    await load();
    toast.success("Hours updated");
  };

  const setSalary = async (uid, salary) => {
    if (!salary) return;
    await api.post("/salary", { user_id: uid, monthly_salary: parseFloat(salary), currency: "INR" });
    toast.success("Salary saved");
    await load();
  };

  const downloadPdf = (uid) => {
    const token = getToken();
    window.open(`${API}/salary/${uid}/pdf/${ym}?auth=${token}`, "_blank");
  };

  const emailSalary = async (uid, defaultEmail) => {
    const to = window.prompt("Email salary statement to:", defaultEmail || "");
    if (!to) return;
    try {
      const { data } = await api.post(`/salary/${uid}/${ym}/email`, { recipient_email: to });
      toast.success(`Salary emailed to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    }
  };

  const approveOne = async (aid) => {
    try { await api.post(`/attendance/${aid}/approve`); toast.success("Approved"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approveAll = async () => {
    if (!window.confirm("Approve all pending self-marked attendance entries?")) return;
    try {
      const { data } = await api.post("/attendance/approve-all");
      toast.success(`Approved ${data.approved} entries`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cells = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const totals = useMemo(() => {
    const t = { present: 0, leave: 0, holiday: 0, pending: 0 };
    for (const a of Object.values(byDate)) {
      if (t[a.status] !== undefined) t[a.status] += 1;
      if (a.self_marked && !a.approved_by_founder) t.pending += 1;
    }
    return t;
  }, [byDate]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">People</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><ClipboardCheck className="text-brand" /> Attendance &amp; Salary</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Mark every day as Present, Leave, or Holiday. Salary payable is calculated automatically at month end.</p>
        </div>
      </div>

      {canSeeParticipants && (
        <div className="flex gap-2 border-b border-[color:var(--aii-border)]" data-testid="att-scope-tabs">
          <button onClick={() => setScope("team")} data-testid="att-tab-team" className={`px-4 py-2 text-sm font-semibold border-b-2 ${scope === "team" ? "border-brand text-brand" : "border-transparent text-[color:var(--aii-muted)] hover:text-slate-800"}`}>
            <Users size={13} className="inline mr-1.5"/> Team attendance &amp; salary
          </button>
          <button onClick={() => setScope("participants")} data-testid="att-tab-participants" className={`px-4 py-2 text-sm font-semibold border-b-2 ${scope === "participants" ? "border-brand text-brand" : "border-transparent text-[color:var(--aii-muted)] hover:text-slate-800"}`}>
            <GraduationCap size={13} className="inline mr-1.5"/> Participants — volunteer hours
          </button>
        </div>
      )}

      {scope === "participants" ? <ParticipantAttendanceTab/> : (
        <>
          <MonthNavigator cursor={cursor} setCursor={setCursor} jump={jump}
            showUserSelector={isAdmin && canSeeOthers} staffOptions={staffOptions}
            selectedUser={selectedUser} setSelectedUser={setSelectedUser}/>
          <AttendanceCalendar cells={cells} cursor={cursor} byDate={byDate} totals={totals}
            onMark={mark} onEditHours={editHours} isFounder={user.real_role === "FOUNDER"}/>
          <SalaryTable summary={summary} isAdmin={isAdmin} monthLabel={`${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
            onSalaryChange={setSalary} onDownloadPdf={downloadPdf} onEmailSalary={emailSalary}/>
        </>
      )}
    </div>
  );
}

function MonthNavigator({ cursor, setCursor, jump, showUserSelector, staffOptions, selectedUser, setSelectedUser }) {
  return (
    <div className="flex items-center gap-2">
      <button data-testid="att-prev" onClick={() => jump(-1)} className="p-1.5 rounded-md hover:bg-[color:var(--aii-green-tint)]"><ChevronLeft size={16}/></button>
      <div className="font-semibold text-lg">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
      <button data-testid="att-next" onClick={() => jump(1)} className="p-1.5 rounded-md hover:bg-[color:var(--aii-green-tint)]"><ChevronRight size={16}/></button>
      <button onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)]">This month</button>
      {showUserSelector && (
        <select data-testid="att-user" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
          className="ml-auto px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
          {staffOptions.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
        </select>
      )}
    </div>
  );
}

function AttendanceCalendar({ cells, cursor, byDate, totals, onMark, onEditHours, isFounder }) {
  const todayISO = iso(new Date());
  return (
    <div className="aii-card p-4">
      <div className="grid grid-cols-7 gap-px bg-[color:var(--aii-border)] rounded-md overflow-hidden border border-[color:var(--aii-border)]">
        {DOW.map(d => (
          <div key={d} className="bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] text-xs font-semibold uppercase tracking-wider p-2 text-center">{d}</div>
        ))}
        {cells.map(d => {
          const dateISO = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const status = byDate[dateISO]?.status;
          const meta = status ? STATUS_META[status] : null;
          const isToday = dateISO === todayISO;
          const isSunday = d.getDay() === 0;
          // Past-day lock — mirrors the backend guard added at
          // `routers/attendance.py::mark`. Non-Founder staff can no
          // longer mark yesterday (or older) after the daily cut-off;
          // Founder retains full override for corrections + retros.
          const isPastLocked = !isFounder && dateISO < todayISO;
          return (
            <div key={dateISO} data-testid={`att-cell-${dateISO}`}
              className={`bg-white min-h-[70px] p-1.5 flex flex-col ${inMonth ? "" : "opacity-40"} ${isSunday ? "bg-[color:var(--aii-bg)]" : ""} ${isPastLocked ? "opacity-60" : ""}`}>
              <div className={`text-[11px] font-semibold flex items-center gap-1 ${isToday ? "bg-brand text-white rounded-full w-5 h-5 justify-center" : "text-[color:var(--aii-text-2)]"}`}>
                {d.getDate()}
                {isPastLocked && !status && (
                  <span title="Past days can only be marked by the Founder — ask them to backfill this."
                        className="text-[8px] uppercase tracking-widest text-slate-400 font-normal ml-auto">🔒</span>
                )}
              </div>
              {inMonth && !isSunday && !isPastLocked && (
                <div className="mt-auto flex gap-0.5">
                  {["present", "leave", "holiday"].map(s => (
                    <button key={s} data-testid={`att-mark-${dateISO}-${s}`} onClick={() => onMark(dateISO, s)}
                      title={STATUS_META[s].label}
                      className={`flex-1 text-[10px] font-bold py-1 rounded ${status === s ? STATUS_META[s].color : "bg-[color:var(--aii-bg)] text-[color:var(--aii-muted)] hover:bg-[color:var(--aii-green-tint)]"}`}>
                      {STATUS_META[s].short}
                    </button>
                  ))}
                </div>
              )}
              {isPastLocked && !status && inMonth && !isSunday && (
                <button
                  data-testid={`att-backfill-${dateISO}`}
                  onClick={async () => {
                    // One-tap escalation — routes a Founder approval
                    // request instead of silently letting the day slip.
                    const reason = window.prompt(
                      `Why do you need attendance backfill for ${dateISO}?\n(Founder reviews the request before applying)`,
                      "Forgot to clock in — was on site the whole day.",
                    );
                    if (reason === null) return;
                    try {
                      await api.post("/attendance/request-backfill", {
                        date: dateISO, status: "present", reason,
                      });
                      toast.success("Backfill request sent to Founder");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not send request");
                    }
                  }}
                  title="Request Founder to backfill this day"
                  className="mt-auto text-[9px] font-bold py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300">
                  Request backfill
                </button>
              )}
              {meta && (
                <div className={`text-[9px] font-semibold uppercase tracking-wide mt-1 ${status === "present" ? "text-emerald-700" : status === "leave" ? "text-red-700" : "text-amber-700"}`}
                     title={status === "leave" && byDate[dateISO]?.leave_reason ? `Reason: ${byDate[dateISO].leave_reason}` : (status === "present" && byDate[dateISO]?.check_in ? `${byDate[dateISO].check_in} → ${byDate[dateISO].check_out || "—"}` : meta.label)}>
                  {meta.label}
                  {status === "present" && byDate[dateISO]?.hours != null && (
                    !isPastLocked ? (
                      <button data-testid={`att-hours-${dateISO}`}
                              onClick={(e) => { e.stopPropagation(); onEditHours?.(dateISO); }}
                              title={`Logged ${byDate[dateISO].check_in || "—"} → ${byDate[dateISO].check_out || "—"} · click to edit`}
                              className="ml-1 text-[10px] font-bold text-brand hover:underline">
                        · {byDate[dateISO].hours}h
                      </button>
                    ) : (
                      <span data-testid={`att-hours-locked-${dateISO}`}
                            title={`Logged ${byDate[dateISO].check_in || "—"} → ${byDate[dateISO].check_out || "—"} — locked (past day, Founder only)`}
                            className="ml-1 text-[10px] font-bold text-[color:var(--aii-muted)]">
                        · {byDate[dateISO].hours}h
                      </span>
                    )
                  )}
                  {status === "leave" && byDate[dateISO]?.leave_reason && (
                    <div className="normal-case tracking-normal text-[10px] font-normal text-red-800 truncate mt-0.5">{byDate[dateISO].leave_reason}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-[color:var(--aii-text-2)]">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm"/> Present {totals.present}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm"/> Leave {totals.leave}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-sm"/> Holiday {totals.holiday}</span>
        {!isFounder && (
          <span className="text-[color:var(--aii-muted)] italic">🔒 Past days locked — Founder-only backfill</span>
        )}
        <span className="ml-auto">Sundays are default off</span>
      </div>
    </div>
  );
}

function SalaryTable({ summary, isAdmin, monthLabel, onSalaryChange, onDownloadPdf, onEmailSalary }) {
  return (
    <div className="aii-card overflow-x-auto">
      <div className="p-4 border-b border-[color:var(--aii-border)] flex items-center gap-2">
        <IndianRupee size={16} className="text-brand"/>
        <h3 className="font-semibold">Salary payable — {monthLabel}</h3>
        {isAdmin && <Users size={14} className="text-[color:var(--aii-muted)] ml-auto"/>}
      </div>
      <div className="overflow-x-auto">
        <table className="aii-table">
          <thead><tr>
            <th>Member</th><th>Role</th><th>Working days</th><th>Present</th><th>Leave</th><th>Holiday</th>
            <th>Effective</th><th>Hours logged</th><th>Monthly salary</th><th>Hourly wage</th><th>Payable</th><th></th>
          </tr></thead>
          <tbody>
            {summary.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-[color:var(--aii-muted)]">No data.</td></tr>}
            {summary.map(row => (
              <tr key={row.user_id} data-testid={`sal-row-${row.user_id}`}>
                <td className="font-medium">{row.name}</td>
                <td>{row.role}</td>
                <td>{row.working_days}</td>
                <td className="text-emerald-700 font-semibold">{row.present}</td>
                <td className="text-red-700 font-semibold">{row.leave}</td>
                <td className="text-amber-700 font-semibold">{row.holiday}</td>
                <td className="font-semibold">{row.effective_days}</td>
                <td className="font-semibold" data-testid={`sal-hours-${row.user_id}`}>
                  {row.total_hours ?? 0}h
                  {row.days_missing_hours > 0 && (
                    <span title={`${row.days_missing_hours} day(s) counted as a full ${row.base_hours_per_day || 9}h — no check-in/out logged`}
                          className="ml-1 text-[10px] text-amber-700">*</span>
                  )}
                </td>
                <td>
                  {isAdmin ? (
                    <input data-testid={`sal-set-${row.user_id}`} type="number" defaultValue={row.monthly_salary}
                      onBlur={e => onSalaryChange(row.user_id, e.target.value)}
                      className="w-28 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                  ) : (
                    <span>₹{row.monthly_salary.toLocaleString()}</span>
                  )}
                </td>
                <td className="text-[color:var(--aii-text-2)]" data-testid={`sal-wage-${row.user_id}`}>
                  <div className="text-sm font-semibold">₹{(row.hourly_wage ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}/h</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)]">
                    ₹{(row.daily_salary ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}/day ÷ {row.base_hours_per_day ?? 9}h
                  </div>
                </td>
                <td className="font-display text-brand font-semibold">₹{row.salary_payable.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                <td className="text-right">
                  {isAdmin && (
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`sal-pdf-${row.user_id}`} onClick={() => onDownloadPdf(row.user_id)}
                        className="text-xs flex items-center gap-1 px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">
                        <Download size={11}/> PDF
                      </button>
                      <button data-testid={`sal-email-${row.user_id}`} onClick={() => onEmailSalary(row.user_id, row.email)}
                        className="text-xs flex items-center gap-1 px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">
                        <Mail size={11}/> Email
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ParticipantAttendanceTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/participant-attendance").then(({data}) => setRows(data || [])).catch(()=>{}); }, []);
  const visible = useMemo(
    () => (filter ? rows.filter(r => (r.participant_name || "").toLowerCase().includes(filter.toLowerCase())) : rows),
    [rows, filter]
  );
  const totals = useMemo(() => visible.reduce((acc, r) => {
    const k = r.participant_user_id;
    acc[k] = acc[k] || { name: r.participant_name, hours: 0, days: 0, leaves: 0 };
    if (r.status === "present") { acc[k].hours += (r.hours || 0); acc[k].days += 1; }
    else acc[k].leaves += 1;
    return acc;
  }, {}), [visible]);
  return (
    <div className="space-y-4" data-testid="participant-attendance-panel">
      <div className="flex items-center gap-2">
        <input className="input-aii" placeholder="Search participant name…" value={filter} onChange={e => setFilter(e.target.value)} data-testid="pat-filter"/>
        <div className="text-xs text-[color:var(--aii-muted)]">{visible.length} entr{visible.length === 1 ? "y" : "ies"}</div>
      </div>
      <div className="aii-card p-4">
        <h3 className="font-semibold mb-2">Totals by participant</h3>
        <table className="aii-table text-sm">
          <thead><tr><th>Participant</th><th className="text-right">Days present</th><th className="text-right">Volunteer hours</th><th className="text-right">Leaves</th></tr></thead>
          <tbody>
            {Object.values(totals).length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[color:var(--aii-muted)]">No participant attendance logged yet.</td></tr>}
            {Object.values(totals).map((t, i) => (
              <tr key={`totals-${t.name}-${i}`}><td>{t.name}</td><td className="text-right">{t.days}</td><td className="text-right font-semibold text-brand">{t.hours.toFixed(1)}h</td><td className="text-right">{t.leaves}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="aii-card p-4">
        <h3 className="font-semibold mb-2">Daily log</h3>
        <table className="aii-table text-sm">
          <thead><tr><th>Date</th><th>Participant</th><th>Status</th><th className="text-right">Hours</th><th>Reason / Notes</th></tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[color:var(--aii-muted)]">Nothing logged.</td></tr>}
            {visible.map(r => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td>{r.participant_name}</td>
                <td className="text-xs uppercase font-semibold">{r.status === "present" ? <span className="text-emerald-700">Present</span> : <span className="text-red-700">Leave</span>}</td>
                <td className="text-right">{r.hours || "—"}</td>
                <td className="text-xs text-[color:var(--aii-text-2)]">{r.leave_reason || r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
